import { api, listPages, paged } from '../console/api.ts';
import { empty, field, idRef, loadMore, meter, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { cap, esc, icon, PRODUCT, person, when } from '../console/format.ts';
import { type Actions, type ClickAction, current, render } from '../console/router.ts';
import { canQuote, supervises } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import { selectControl, textareaControl, textControl } from '../controls.ts';
import { type Holder, loadCatalog, setQuoting } from './cotizar.ts';

/** «Prospectos»: the funnel of leads, from captured to won, with their follow-ups and conversion. */

type LeadView = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  productLine: string;
  source: string;
  notes: string;
  agentId: string;
  stage: string;
  createdAt: string;
  closedAt: string | null;
  quoteId: string | null;
  policyNumber: string | null;
  lostReason: string | null;
  possibleDuplicateOf: string[];
  followUp: { action: string; dueAt: string; remindedAt: string | null } | null;
  overdue: boolean;
  daysToClose: number | null;
  timeline: {
    type: 'STAGE' | 'ACTIVITY' | 'ASSIGNED';
    at: string;
    by: string;
    stage?: string;
    kind?: string;
    note?: string;
    quoteId?: string | null;
    policyNumber?: string | null;
    reason?: string | null;
    agentId?: string;
  }[];
};
type Conversion = { leads: number; won: number; lost: number; rate: number };
export type LeadStatsView = {
  funnel: Record<string, number>;
  bySource: Record<string, Conversion>;
  byAgent: Record<string, Conversion>;
  total: Conversion;
  averageDaysToClose: number | null;
  overdueFollowUps: number;
  withoutFollowUp: number;
};
export const LEAD_STAGE: Record<string, [label: string, pill: string]> = {
  NEW: ['Nuevo', 'DRAFT'],
  CONTACTED: ['Contactado', 'info'],
  QUALIFIED: ['Calificado', 'info'],
  QUOTED: ['Cotizado', 'warn'],
  WON: ['Ganado', 'ok'],
  LOST: ['Perdido', 'bad'],
};
export const leadPill = (stage: string) => {
  const [label, cls] = LEAD_STAGE[stage] ?? [stage, 'info'];
  return pill(cls, label);
};
export const LEAD_SOURCE: Record<string, string> = {
  MANUAL: 'Captado por el agente',
  PORTAL: 'Portal web',
  REFERRAL: 'Referido',
  EVENT: 'Evento',
  SOCIAL: 'Redes sociales',
  OTHER: 'Otra fuente',
};
const ACTIVITY: Record<string, string> = {
  CALL: 'Llamada',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Correo',
  MEETING: 'Reunión',
  NOTE: 'Nota',
};
/** The next open stage the agent can move a lead to by hand (QUOTED needs a quote). */
const NEXT_STAGE: Record<string, [to: string, label: string]> = {
  NEW: ['CONTACTED', 'Marcar contactado'],
  CONTACTED: ['QUALIFIED', 'Marcar calificado'],
};
/** The stages of a lead still being worked (the «Abiertos» filter). */
const LEAD_STAGES_OPEN = ['NEW', 'CONTACTED', 'QUALIFIED', 'QUOTED'];
const LEAD_FILTERS: [key: string, label: string][] = [
  ['OPEN', 'Abiertos'],
  ['OVERDUE', 'Seguimiento vencido'],
  ['NEW', 'Nuevos'],
  ['CONTACTED', 'Contactados'],
  ['QUALIFIED', 'Calificados'],
  ['QUOTED', 'Cotizados'],
  ['WON', 'Ganados'],
  ['LOST', 'Perdidos'],
  ['ALL', 'Todos'],
];
let leadFilter = 'OPEN';
let openLeadId: string | null = null;
/** A new session starts with the open prospects and none of them open. */
export function resetLeads() {
  leadFilter = 'OPEN';
  openLeadId = null;
}

const pct = (r: number) => `${Math.round(r * 100)} %`;
/** Tomorrow at 09:00, for the follow-up input (datetime-local, the browser's time). */
const tomorrowAtNine = () => {
  const d = new Date(Date.now() + 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
};

export function leadStatsHtml(s: LeadStatsView, manage: boolean) {
  const open =
    (s.funnel.NEW ?? 0) +
    (s.funnel.CONTACTED ?? 0) +
    (s.funnel.QUALIFIED ?? 0) +
    (s.funnel.QUOTED ?? 0);
  const top = Math.max(1, ...Object.values(s.funnel));
  const funnel = ['NEW', 'CONTACTED', 'QUALIFIED', 'QUOTED', 'WON', 'LOST']
    .map(
      (st) =>
        `<li><span class="funnel-label">${esc(LEAD_STAGE[st]?.[0] ?? st)}</span>${meter((s.funnel[st] ?? 0) / top, st === 'LOST' ? 'late' : '')}<span class="num">${s.funnel[st] ?? 0}</span></li>`,
    )
    .join('');
  const rows = (r: Record<string, Conversion>, name: (k: string) => string) =>
    Object.entries(r)
      .sort((a, b) => b[1].rate - a[1].rate || b[1].leads - a[1].leads)
      .map(
        ([k, c]) =>
          `<tr><td>${esc(name(k))}</td><td class="num">${c.leads}</td><td class="num">${c.won}</td><td class="num">${pct(c.rate)}</td></tr>`,
      )
      .join('');
  const table = (title: string, first: string, body: string) =>
    body
      ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">${esc(title)}</caption>
        <thead><tr><th>${first}</th><th class="num">Prospectos</th><th class="num">Ganados</th><th class="num">Conversión</th></tr></thead><tbody>${body}</tbody></table></div>`
      : '';
  return `<section class="kpis" aria-label="Prospectos">
    <div class="card kpi"><span class="kpi-label">${icon('users')}Abiertos</span><span class="kpi-value">${open}</span><span class="kpi-foot">${s.withoutFollowUp ? `${s.withoutFollowUp} sin siguiente acción` : 'Todos con siguiente acción'}</span></div>
    <div class="card kpi ${s.overdueFollowUps ? 'warn' : ''}"><span class="kpi-label">${icon('warn')}Seguimientos vencidos</span><span class="kpi-value">${s.overdueFollowUps}</span><span class="kpi-foot">${s.overdueFollowUps ? 'Llámalos hoy' : 'Todo al día'}</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('trend')}Conversión</span><span class="kpi-value">${pct(s.total.rate)}</span><span class="kpi-foot">${s.total.won} ganados de ${s.total.leads} captados</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('ok')}Días para cerrar</span><span class="kpi-value">${s.averageDaysToClose === null ? '—' : Math.round(s.averageDaysToClose * 10) / 10}</span><span class="kpi-foot">promedio de captado a ganado</span></div>
  </section>
  <section class="card lead-stats"><div class="card-head"><h2>Embudo</h2><span class="spacer"></span><span class="muted small">${manage ? 'Toda la agencia' : 'Tus prospectos'}</span></div>
    <div class="lead-stats-grid"><ol class="funnel" aria-label="Prospectos por etapa">${funnel}</ol>
    <div>${table(
      'Conversión por fuente',
      'Fuente',
      rows(s.bySource, (k) => LEAD_SOURCE[k] ?? k),
    )}
    ${manage ? table('Conversión por agente', 'Agente', rows(s.byAgent, cap)) : ''}</div></div></section>`;
}

function leadDetail(l: LeadView, agents: string[], manage: boolean) {
  const closed = !!l.closedAt;
  const entry = (e: LeadView['timeline'][number]) => {
    const meta = `<span class="muted small">${esc(when(e.at))} · ${esc(person(e.by))}</span>`;
    if (e.type === 'ACTIVITY')
      return `<li>${pill('info', ACTIVITY[e.kind ?? ''] ?? e.kind ?? '')}${meta}<span class="timeline-note">${esc(e.note)}</span></li>`;
    if (e.type === 'ASSIGNED')
      return `<li>${pill('warn', 'Reasignado')}${meta}<span class="timeline-note">Ahora lo lleva ${esc(cap(e.agentId ?? ''))}</span></li>`;
    const extra =
      e.quoteId && e.stage === 'QUOTED'
        ? `Cotización <b>${idRef(e.quoteId)}</b>`
        : e.policyNumber
          ? `Póliza <b class="mono">${esc(e.policyNumber)}</b>`
          : esc(e.reason ?? '');
    return `<li>${leadPill(e.stage ?? '')}${meta}${extra ? `<span class="timeline-note">${extra}</span>` : ''}</li>`;
  };
  const next = l.followUp
    ? `<p class="lead-next ${l.overdue ? 'late' : ''}">${icon(l.overdue ? 'warn' : 'bell')}<span><strong>${esc(l.followUp.action)}</strong>
      <span class="small">${l.overdue ? 'Vencido desde' : 'Para el'} ${esc(when(l.followUp.dueAt))}</span></span></p>`
    : closed
      ? ''
      : `<p class="lead-next">${icon('alert')}<span>Sin siguiente acción. Agenda cuándo vuelves a contactarlo.</span></p>`;
  const id = esc(l.id);
  const stageButtons: string[] = [];
  const step = NEXT_STAGE[l.stage];
  if (step)
    stageButtons.push(
      `<button type="button" class="btn" data-lead-stage="${id}" data-to="${step[0]}">${step[1]}</button>`,
    );
  if (canQuote())
    stageButtons.push(
      `<button type="button" class="btn primary" data-lead-quote="${id}" data-name="${esc(l.name)}">${icon('bolt')}Cotizar</button>`,
    );
  const actions = closed
    ? `<p class="muted small lead-closed">${icon('lock')}Prospecto cerrado: ya no cambia.</p>`
    : `<div class="toolbar order-actions">
        ${field(
          'Contacto',
          selectControl(
            { id: `la-kind-${id}` },
            Object.entries(ACTIVITY).map(([k, v]) => ({ value: k, label: v })),
          ),
        )}
        <div class="field grow">${textControl({ label: 'Qué se habló', id: `la-note-${id}`, maxlength: 2000, placeholder: 'Pide cotizar un Corolla 2023' })}</div>
        <div class="order-buttons"><button type="button" class="btn" data-lead-log="${id}">Registrar</button></div></div>
      <div class="toolbar">
        <div class="field grow">${textControl({ label: 'Siguiente acción', id: `lf-action-${id}`, maxlength: 200, placeholder: 'Llamar para presentar la cotización', value: l.followUp?.action ?? '' })}</div>
        ${field('Cuándo', textControl({ id: `lf-due-${id}`, type: 'datetime-local', value: tomorrowAtNine() }))}
        <div class="order-buttons"><button type="button" class="btn" data-lead-follow="${id}">Agendar</button></div></div>
      <div class="toolbar">
        ${l.stage === 'QUOTED' ? field('Póliza emitida', textControl({ id: `lw-policy-${id}`, class: 'mono', placeholder: 'VOL-AUTO-2026-000123' })) : ''}
        ${l.stage === 'QUALIFIED' || l.stage === 'CONTACTED' || l.stage === 'NEW' ? field('Cotización hecha fuera de Nubi', textControl({ id: `lq-quote-${id}`, class: 'mono', placeholder: 'Número de cotización' })) : ''}
        <div class="field grow">${textControl({ label: 'Motivo si se pierde', id: `ll-reason-${id}`, maxlength: 500, placeholder: 'Renovó con otra aseguradora' })}</div>
        <div class="order-buttons">${stageButtons.join('')}
          ${l.stage !== 'QUOTED' ? `<button type="button" class="btn" data-lead-quoted="${id}">Registrar cotización</button>` : ''}
          ${l.stage === 'QUOTED' ? `<button type="button" class="btn primary" data-lead-stage="${id}" data-to="WON">Marcar ganado</button>` : ''}
          <button type="button" class="btn danger" data-lead-stage="${id}" data-to="LOST">Marcar perdido</button></div></div>
      ${
        manage
          ? `<div class="toolbar">${field(
              'Reasignar a',
              selectControl(
                { id: `lr-agent-${id}` },
                agents.filter((a) => a !== l.agentId).map((a) => ({ value: a, label: cap(a) })),
              ),
            )}
        <div class="order-buttons"><button type="button" class="btn" data-lead-reassign="${id}">Reasignar</button></div></div>`
          : ''
      }`;
  return `<article class="card lead-detail" id="lead-detail" tabindex="-1"><div class="card-head">
      <div class="policy-title"><strong>${esc(l.name)}</strong>
        <div class="policy-meta"><span class="mono">${id}</span><span>${esc(PRODUCT[l.productLine] ?? l.productLine)}</span><span>${esc(LEAD_SOURCE[l.source] ?? l.source)}</span>
        ${l.phone ? `<span>${esc(l.phone)}</span>` : ''}${l.email ? `<span>${esc(l.email)}</span>` : ''}<span>Agente <b>${esc(cap(l.agentId))}</b></span>
        ${l.quoteId ? `<span>Cotización <b>${idRef(l.quoteId)}</b></span>` : ''}${l.policyNumber ? `<span>Póliza <b class="mono">${esc(l.policyNumber)}</b></span>` : ''}</div></div>
      <span class="spacer"></span>${leadPill(l.stage)}
      <button type="button" class="btn sm" data-lead-close aria-label="Cerrar el detalle">Cerrar</button></div>
    ${l.possibleDuplicateOf.length ? `<p class="lead-dup">${icon('warn')}Posible duplicado: el mismo teléfono o correo ya está en ${l.possibleDuplicateOf.map((d) => `<b class="mono">${esc(d)}</b>`).join(', ')}.</p>` : ''}
    ${l.notes ? `<p class="order-detail">${esc(l.notes)}</p>` : ''}
    ${l.lostReason ? `<p class="order-detail">Se perdió: ${esc(l.lostReason)}</p>` : ''}
    ${next}
    <ol class="timeline">${l.timeline.map(entry).join('')}</ol>
    ${actions}</article>`;
}

export async function prospectos() {
  const manage = supervises();
  const [leadPages, stats, agents] = await Promise.all([
    // The API filters by stage, so each page holds only what the chosen filter shows.
    paged<LeadView>('prospectos', `/v1/leads?stage=${encodeURIComponent(leadFilter)}`),
    api<LeadStatsView>('GET', '/v1/leads/stats'),
    manage
      ? api<{ agentId: string }[]>('GET', '/v1/agents').then((a) => a.map((x) => x.agentId))
      : Promise.resolve([] as string[]),
  ]);
  let html = pageHead(
    'Prospectos',
    manage
      ? 'Los prospectos de todos los agentes: en qué etapa van, cuál es su siguiente acción y qué fuente convierte mejor.'
      : 'Tus prospectos, desde que los captas hasta que los ganas: qué hablaste con cada uno y cuándo vuelves a llamarlo.',
  );
  html += leadStatsHtml(stats, manage);
  html += `<form id="lead-form" class="card" novalidate><div class="card-head"><h2>Captar un prospecto</h2></div>
    <div class="form-grid">
      <fieldset><legend>Quién es</legend>
        ${field('Nombre', textControl({ id: 'l-name', maxlength: 160, autocomplete: 'off', placeholder: 'Pedro Gómez' }))}
        ${field('Teléfono', textControl({ id: 'l-phone', type: 'tel', inputmode: 'tel', maxlength: 40, placeholder: '5555-1234' }))}
        ${field('Correo', textControl({ id: 'l-email', type: 'email', maxlength: 160, placeholder: 'pedro@correo.com' }))}
      </fieldset>
      <fieldset><legend>Qué busca</legend>
        ${field(
          'Producto',
          selectControl(
            { id: 'l-product' },
            ['AUTO', 'PROPERTY', 'LIFE', 'HEALTH'].map((p) => ({
              value: p,
              label: PRODUCT[p] ?? p,
            })),
          ),
        )}
        ${field(
          'Fuente',
          selectControl(
            { id: 'l-source' },
            Object.entries(LEAD_SOURCE).map(([k, v]) => ({ value: k, label: v })),
          ),
        )}
        ${
          manage
            ? field(
                'Agente',
                selectControl(
                  { id: 'l-agent' },
                  agents.map((a) => ({ value: a, label: cap(a) })),
                ),
              )
            : ''
        }
      </fieldset>
      <fieldset><legend>Notas</legend>
        ${field('Lo que sabes', textareaControl({ id: 'l-notes', rows: 4, maxlength: 2000, placeholder: 'Tiene un Corolla 2023; su póliza vence en noviembre.' }))}
      </fieldset>
    </div>
    <div class="form-foot"><span class="muted small">Con teléfono o correo. Si ya hay un prospecto abierto con los mismos datos, lo marcamos como posible duplicado.</span>
      <button class="btn primary" type="submit" id="lead-submit">Guardar prospecto</button></div></form>`;
  const shown = leadPages.items;
  // The open lead may be on a page not loaded or outside the filter: read it by itself.
  const selected =
    shown.find((l) => l.id === openLeadId) ??
    (openLeadId
      ? await api<LeadView>('GET', `/v1/leads/${encodeURIComponent(openLeadId)}`).catch(() => null)
      : null);
  if (selected) html += leadDetail(selected, agents, manage);
  // Every count comes from the funnel's numbers (all the leads, not the pages loaded).
  const openCount = LEAD_STAGES_OPEN.reduce((a, k) => a + (stats.funnel[k] ?? 0), 0);
  const total = Object.values(stats.funnel).reduce((a, n) => a + n, 0);
  if (!total)
    return (
      html +
      empty(
        'users',
        'Todavía no hay prospectos',
        'Capta el primero con el formulario de arriba: nombre, teléfono o correo y qué seguro busca.',
      )
    );
  const count = (k: string) =>
    k === 'ALL'
      ? total
      : k === 'OPEN'
        ? openCount
        : k === 'OVERDUE'
          ? stats.overdueFollowUps
          : (stats.funnel[k] ?? 0);
  const filters = `<div class="lead-filters" role="group" aria-label="Filtrar por etapa">${LEAD_FILTERS.map(
    ([k, label]) =>
      `<button type="button" class="btn sm" data-lead-filter="${k}" aria-pressed="${k === leadFilter}">${label} <span class="muted">${count(k)}</span></button>`,
  ).join('')}</div>`;
  const rows = shown
    .map(
      (
        l,
      ) => `<tr${l.id === openLeadId ? ' aria-current="true"' : ''}><td><strong>${esc(l.name)}</strong>${l.possibleDuplicateOf.length ? ` ${pill('warn', 'Posible duplicado')}` : ''}
        <div class="muted small">${esc([l.phone, l.email].filter(Boolean).join(' · '))}</div></td>
        <td>${esc(PRODUCT[l.productLine] ?? l.productLine)}</td><td>${esc(LEAD_SOURCE[l.source] ?? l.source)}</td>
        ${manage ? `<td>${esc(cap(l.agentId))}</td>` : ''}
        <td>${leadPill(l.stage)}</td>
        <td>${l.followUp && !l.closedAt ? `${l.overdue ? `<strong class="late-text">Vencido · ${esc(when(l.followUp.dueAt))}</strong>` : esc(when(l.followUp.dueAt))}<div class="muted small">${esc(l.followUp.action)}</div>` : '<span class="muted">—</span>'}</td>
        <td class="actions"><button type="button" class="btn sm" data-lead-open="${esc(l.id)}">Abrir</button></td></tr>`,
    )
    .join('');
  html += `<section class="card"><div class="card-head"><h2>Embudo de prospectos</h2></div>${filters}
    ${
      shown.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Prospecto</th><th>Producto</th><th>Fuente</th>${manage ? '<th>Agente</th>' : ''}<th>Etapa</th><th>Siguiente acción</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
      <tbody>${rows}</tbody></table></div>${loadMore('prospectos', leadPages.more, shown.length, 'prospectos')}`
        : `<p class="muted lead-none">Ningún prospecto en «${esc(LEAD_FILTERS.find((f) => f[0] === leadFilter)?.[1] ?? '')}».</p>`
    }</section>`;
  return html;
}

/** A lead's contact, follow-up, stage, outside quote or new agent (one form per lead). */
const updateLead: ClickAction = (t, d) => {
  if (!(d.leadLog || d.leadFollow || d.leadStage || d.leadQuoted || d.leadReassign)) return;
  const id = (d.leadLog ?? d.leadFollow ?? d.leadStage ?? d.leadQuoted ?? d.leadReassign) as string;
  const input = (prefix: string) =>
    document.getElementById(`${prefix}-${id}`) as HTMLInputElement | null;
  const value = (prefix: string) => input(prefix)?.value.trim() ?? '';
  const path = `/v1/leads/${encodeURIComponent(id)}`;
  void act(t, 'Guardando…', async () => {
    let l: LeadView;
    let done: string;
    if (d.leadLog) {
      const note = value('la-note');
      if (!note) throw new InputError('Escribe qué se habló con el prospecto.');
      l = await api<LeadView>('POST', `${path}/activities`, { kind: value('la-kind'), note });
      done = 'Contacto registrado en la bitácora.';
    } else if (d.leadFollow) {
      const action = value('lf-action');
      const due = value('lf-due');
      if (!action) throw new InputError('Escribe cuál es la siguiente acción.');
      if (!due) throw new InputError('Elige cuándo toca la siguiente acción.');
      l = await api<LeadView>('POST', `${path}/follow-up`, {
        action,
        dueAt: new Date(due).toISOString(),
      });
      done = `Siguiente acción agendada para el ${when(l.followUp?.dueAt ?? '')}.`;
    } else if (d.leadQuoted) {
      const quoteId = value('lq-quote');
      if (!quoteId) throw new InputError('Escribe el número de la cotización.');
      l = await api<LeadView>('POST', `${path}/stage`, { to: 'QUOTED', quoteId });
      done = `Cotización ${quoteId} registrada.`;
    } else if (d.leadReassign) {
      const agentId = value('lr-agent');
      if (!agentId) throw new InputError('Elige a qué agente se lo pasas.');
      l = await api<LeadView>('POST', `${path}/reassign`, { agentId });
      done = `Prospecto reasignado a ${cap(agentId)}; le avisamos.`;
    } else {
      const to = d.to as string;
      const reason = value('ll-reason');
      if (to === 'LOST' && !reason) throw new InputError('Escribe por qué se perdió el prospecto.');
      const policyNumber = value('lw-policy');
      l = await api<LeadView>('POST', `${path}/stage`, {
        to,
        ...(to === 'LOST' ? { reason } : {}),
        ...(to === 'WON' && policyNumber ? { policyNumber } : {}),
      });
      done = `Prospecto en «${LEAD_STAGE[l.stage]?.[0] ?? l.stage}».`;
    }
    await render(current, { quiet: true });
    return done;
  });
};

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  click: {
    leadFilter: (_, d) => {
      if (!d.leadFilter) return;
      leadFilter = d.leadFilter;
      delete listPages.prospectos;
      void render(current, { quiet: true });
    },
    leadOpen: (_, d) => {
      if (!d.leadOpen) return;
      openLeadId = d.leadOpen;
      void render(current, { quiet: true }).then(() => $('#lead-detail')?.focus());
    },
    leadClose: () => {
      openLeadId = null;
      void render(current, { quiet: true });
    },
    leadQuote: (t, d) => {
      if (!d.leadQuote) return;
      const leadId = d.leadQuote;
      void act(t, 'Preparando la cotización del prospecto…', async () => {
        // The catalog line of the prospect's product, its questionnaire and what it told us.
        const draft = await api<{
          productLine: string;
          name: string;
          policyholder: Partial<Holder>;
        }>('GET', `/v1/leads/${encodeURIComponent(leadId)}/quote-draft`);
        const lead = { id: leadId, name: d.name ?? draft.name };
        setQuoting({
          quoteLead: lead,
          // A prospect's quote is never linked to the customer quoted before.
          quoteParty: null,
          holderDraft: null,
          quoteLine: draft.productLine,
          quoteHolder: draft.policyholder,
          lastQuote: null,
          quoteDraft: null,
          fieldErrors: {},
        });
        await render('cotizar');
        const line = (await loadCatalog()).find((l) => l.productLine === draft.productLine);
        return `Cotizando ${line?.name ?? draft.productLine} para ${lead.name}.`;
      });
    },
    goLead: (_, d) => {
      if (!d.goLead) return;
      openLeadId = d.goLead;
      leadFilter = 'ALL';
      delete listPages.prospectos;
      void render('prospectos').then(() => $('#lead-detail')?.focus());
    },
    leadLog: updateLead,
    leadFollow: updateLead,
    leadStage: updateLead,
    leadQuoted: updateLead,
    leadReassign: updateLead,
  },
  submit: {
    'lead-form': (ev) => {
      ev.preventDefault();
      void act($('#lead-submit'), 'Guardando el prospecto…', async () => {
        const value = (sel: string) => ($(sel) as HTMLInputElement | null)?.value.trim() ?? '';
        const phone = value('#l-phone');
        const email = value('#l-email');
        if (!phone && !email)
          throw new InputError('Escribe el teléfono o el correo del prospecto.');
        const l = await api<LeadView>('POST', '/v1/leads', {
          name: textInput('#l-name', 'el nombre del prospecto'),
          phone: phone || null,
          email: email || null,
          productLine: value('#l-product'),
          source: value('#l-source'),
          notes: value('#l-notes'),
          ...(value('#l-agent') ? { agentId: value('#l-agent') } : {}),
        });
        openLeadId = l.id;
        await render(current, { quiet: true });
        $('#lead-detail')?.focus();
        return l.possibleDuplicateOf.length
          ? `${l.name} guardado; revisa: puede ser duplicado de ${l.possibleDuplicateOf.join(', ')}.`
          : `${l.name} guardado como prospecto nuevo.`;
      });
      return;
    },
  },
};
